import { AxiosRequestConfig, AxiosResponse } from "axios";
import axios, { AxiosInstance } from "axios";
import { ACCOUNT_API_REFRESH_TOKEN } from "shared/constants/api-endpoint";
import { getCurrentCulture } from "./localization";
import { store } from "app.store";
import { login, logout } from "states/account/account.slice";

class ApiClient {
  constructor() {
    this.instance = axios.create({
      baseURL: process.env.REACT_APP_API_URI,
    });
    this.requestInterceptor();
    this.reponseInterceptor();
  }

  instance: AxiosInstance;
  failedQueue: any[] = [];
  isRefreshing = false;

  processQueue(error: any) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    this.failedQueue = [];
  }

  private requestInterceptor() {
    this.instance.interceptors.request.use(
      (config) => {
        if (config.headers.protected) {
          const token = store.getState().account.token.accessToken;
          config = {
            ...config,
            headers: { ...config?.headers, Authorization: "Bearer " + token },
          };
        }
        return {
          ...config,
          headers: {
            ...config.headers,
            "Accept-Language": getCurrentCulture(),
          },
        };
      },
      (error) => {
        Promise.reject(error);
      }
    );
  }

  private reponseInterceptor() {
    const that = this;
    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async function (error) {
        const originalRequest = error.config;
        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest.url.endsWith(ACCOUNT_API_REFRESH_TOKEN) &&
          originalRequest.headers.protected &&
          !originalRequest.headers._retry
        ) {
          if (that.isRefreshing) {
            return new Promise((resolve, reject) => {
              that.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                originalRequest.headers = {
                  ...originalRequest.headers,
                  _retry: true,
                };
                return that.instance.request(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest.headers = {
            ...originalRequest.headers,
            _retry: true,
          };
          that.isRefreshing = true;

          const refreshToken = store.getState().account.token.refreshToken;
          if (refreshToken) {
            try {
              const res = await that.postHttp(
                ACCOUNT_API_REFRESH_TOKEN,
                {
                  token: refreshToken,
                },
                undefined,
                false
              );
              store.dispatch(login(res.data));
              that.isRefreshing = false;
              that.processQueue(null);
              return await that.instance.request(originalRequest);
            } catch (e) {
              that.processQueue(e);
              that.isRefreshing = false;
              store.dispatch(logout());
              return Promise.reject(error);
            }
          }
          that.isRefreshing = false;
          store.dispatch(logout());
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
  }

  getHttp<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
    withCredentials = true
  ): Promise<R> {
    return this.instance.get<T, R>(url, {
      ...config,
      headers: { protected: withCredentials },
    });
  }

  postHttp<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    withCredentials = true
  ): Promise<R> {
    return this.instance.post<T, R>(url, data, {
      ...config,
      headers: { protected: withCredentials },
    });
  }

  putHttp<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    withCredentials = true
  ): Promise<R> {
    return this.instance.put<T, R>(url, data, {
      ...config,
      headers: { protected: withCredentials },
    });
  }

  deleteHttp<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
    withCredentials = true
  ): Promise<R> {
    return this.instance.delete<T, R>(url, {
      ...config,
      headers: { protected: withCredentials },
    });
  }
}

export const apiClient = new ApiClient();
